import {
    DescriptionDetailState,
    DescriptionState,
    ComplementaryChildrenAgeSegmentState,
    ImagesDetailState,
    ImageState,
    NoteDetailState,
    PROGRESS_MAIN_INFORMATION_TAB,
    PROGRESS_PUBLIC_PORTRAIT_TAB,
    PROGRESS_ROOM_CONFIGURATION_TAB,
    PROGRESS_STEP,
    ProgressArrayItem,
    ProgressByStepState,
    ProgressInfo,
    ProgressState,
    PropertyState,
    RoomState,
    URL_STEP,
    ValidationErrorsState,
} from '../../types/properties/propertyTypes';
import encodeBase64 from '../encodeBase64';
import { getRandomNumber } from '../usefulFunctions';
import { TagColor } from '../../muiComponents/Tag';
import { AppLanguage } from '../../types/appTypes';
import serviceType_attributeDefinitions from '../viewModelStructures/serviceType_AttributeDefinitions';

export const URL_ID_KEY = 'idEncoded';
export const URL_ROOM_ID_KEY = 'idRoomEncoded';
export const HOTELS_LIST_URL = '/owner-portal/your-properties';
export const URL_MAIN_INFO = '/management';
export const URL_PUBLIC_PORTRAIT = '/public-portrait';
export const URL_INVENTORY_LIST = '/inventory';
export const URL_TAB_PARAM = (tab: number) => `?tab=${encodeBase64(tab)}`;
export const URL_HOTEL_BASE = (id: number | string) =>
    `/your-property/${encodeBase64(id)}`;
export const ZITONEO = '/zitoneo';
export const ZITONEO_SERVICE_ATTRIBUTES = '/service-attributes';
export const ZITONEO_PROPERTY_ATTRIBUTES = '/property-attributes';

export const GAELIC_CONTENT_LANGUAGE_KEY = 'GAELIC';
export const ENGLISH_CONTENT_LANGUAGE_KEY = 'ENGLISH';
export const GAELIC_CONTENT_LANGUAGE_ID = 1;
export const ENGLISH_CONTENT_LANGUAGE_ID = 2;
export const BEDROOM_IMAGE_TYPE_KEY = 'BEDROOM';

export const getChildrenAgeSegmentSortOrder = (
    segment: ComplementaryChildrenAgeSegmentState
) =>
    segment.isBaby
        ? 0
        : segment.isFirstChild
        ? 1
        : segment.isSecondChild
        ? 2
        : segment.isOtherChild
        ? 3
        : 4;

export const singleColumnGrid = { xs: 12, sm: 12, md: 6, lg: 6, xl: 3 };
export const doubleColumnGrid = { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 };
export const fullColumnGrid = { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 };
export const imageColumnGrid = { xs: 12, sm: 6, md: 6, lg: 6, xl: 3 };
export const imageSmallColumnGrid = { xs: 12, sm: 6, md: 4, lg: 3, xl: 2 };

export const IMAGE_MAX_SIZE_ALLOWED = 10_485_760; // 10MB = 10 * 1024 *1024 bytes = 26214400
export const IMAGE_MAX_SIXE_ALLOWED_MB = 10;
export const MIN_IMAGE_WIDTH = 840;
export const MIN_IMAGE_HEIGHT = 460;

export const supplierRoomViewModelModel = {
    AdultOnly: true,
    FreeBaby1: true,
    FreeBaby2: true,
    Disabled: true,
    Active: true,
    Name: true,
    Occupancy: true,
    StandardOccupancy: true,
    MinAdults: true,
    MaxAdults: true,
    MinChildren: true,
    MaxChild: true,
    Quantity: true,
    Size: true,
    DateLastUpdated: true,
    Services: {
        Id: true,
        Caption: true,
        LocalizedCaption: true,
        Name: true,
        Order: true,
        Free: true,
    },
    SupplierHotelRoomTypesImages: {
        Id: true,
        Caption: true,
        Feature: true,
        Is360: true,
        Published: true,
        IdHotelImageType: true,
        HotelImageType: { Id: true, Key: true },
        VirtualPath: true,
        IdSupplierHotelRoomType: true,
    },
    SupplierHotelRoomBedTypes: {
        Id: true,
        Caption: true,
        LocalizedCaption: true,
        Name: true,
    },
    SupplierHotelRoomTypeDescriptions: {
        DescriptionType: {
            Key: true,
        },
        Description: true,
        IdContentLanguage: true,
        ContentLanguage: {
            CultureId: true,
            LocalizedCaption: true,
            Caption: true,
            Name: true,
        },
    },
};

export const supplierHotelViewModelModel = {
    Id: true,
    Name: true,
    Progress: true,
    DateLastUpdated: true,
    IdSupplier: true,
    Descriptions: {
        DescriptionType: {
            Key: true,
        },
        Description: true,
        IdContentLanguage: true,
        ContentLanguage: {
            CultureId: true,
            LocalizedCaption: true,
            Caption: true,
            Name: true,
        },
    },
    Services: {
        Id: true,
        Caption: true,
        LocalizedCaption: true,
        Order: true,
        Free: true,
    },
    HostSegments: {
        Id: true,
        Caption: true,
        LocalizedCaption: true,
        Order: true,
    },
    SupplierHotelMealPlans: {
        Id: true,
        Name: true,
        MappingCode: true,
        Enabled: true,
        MealPlanType: {
            Id: true,
            Caption: true,
            LocalizedCaption: true,
            Name: true,
            MappingCodeTemplate: true,
        },
    },
    SupplierHotelRoomTypes: supplierRoomViewModelModel,
    SupplierHotelContentWizardProgress: {
        Id: true,
        Step: true,
        Tab: true,
        Completed: true,
    },
    SupplierHotelImages: {
        Id: true,
        Caption: true,
        Feature: true,
        Is360: true,
        Published: true,
        IdHotelImageType: true,
        HotelImageType: { Id: true, Key: true },
        VirtualPath: true,
        IdSupplierHotelRoomType: true,
    },
    ChildrenAgeSegments: {
        Id: true,
        Key: true,
        Age: true,
        LocalizedCaption: true,
        Caption: true,
        Name: true,
        IsBaby: true,
        IsFirstChild: true,
        IsOtherChild: true,
        IsSecondChild: true,
    },
    IdHotel: true,
    Hotel: {
        Id: true,
        IdHotelType: true,
        HotelType: {
            Caption: true,
            LocalizedCaption: true,
            Name: true,
        },
        IdHotelCategory: true,
        HotelCategory: {
            Caption: true,
            LocalizedCaption: true,
            Name: true,
            RatingNumber: true,
        },
        IdHotelChain: true,
        IdCurrency: true,
        IdCity: true,
        City: {
            Id: true,
            Name: true,
        },
        IdProvince: true,
        Province: {
            Id: true,
            Name: true,
        },
        IdRegion: true,
        Region: {
            Id: true,
            Name: true,
        },
        IdCountry: true,
        Country: {
            Id: true,
            Name: true,
        },
        IdTimeZoneUtil: true,
        Name: true,
        GeneralPhone: true,
        GeneralEmail: true,
        BookingPhone: true,
        BookingEmail: true,
        AccountingPhone: true,
        AccountingEmail: true,
        Address: true,
        CheckInHour: true,
        CheckOutHour: true,
        PostalCode: true,
        HotelCodes: { Id: true, NearbyAirportCode: true },
        Rooms: true,
        AdultsOnly: true,
        AdultAge: true,
        SupplierHotels: {
            IdSupplierHotel: true,
            Primary: true,
        },
        AttributeDefinitionEntities: {
            IdAttributeDefinition: true,
            AttributeDefinition: {
                Name: true,
                FieldType: {
                    IsBoolean: true,
                    IsDate: true,
                    IsNumeric: true,
                    IsString: true,
                },
                MustBeUnique: true,
                AttributeDefinitionSizeType: {
                    Key: true,
                    Size: true,
                },
                IdAttributeDefinitionCategory: true,
                AttributeDefinitionCategory: {
                    Id: true,
                    Name: true,
                    Key: true,
                    Enabled: true,
                    LocalizedCaption: true,
                    Caption: true,
                },
            },
            Required: true,
            Enabled: true,
        },
        AttributeDefinitionValues: {
            IdAttributeDefinition: true,
            Value: true,
        },
        HotelServiceTypes: {
            Enabled: true,
            IdServiceType: true,
            ServiceType: {
                Enabled: true,
                Caption: true,
                LocalizedCaption: true,
                Name: true,
                IdServiceCategory: true,
                ServiceCategory: {
                    Caption: true,
                    LocalizedCaption: true,
                    Name: true,
                    Enabled: true,
                },
            },
            ...serviceType_attributeDefinitions,
            AttributeDefinitionValues: {
                IdAttributeDefinition: true,
                Value: true,
            },
        },
    },
    SpecialNotes: {
        Id: true,
        Message: true,
        Enabled: true,
        FromDate: true,
        ToDate: true,
        FromBookingDate: true,
        DateLastUpdated: true,
    },
    SupplierTaxes: {
        Id: true,
        Name: true,
        Enabled: true,
        Description: true,
        IdSupplierHotelRoomType: true,
        SupplierHotelRoomType: {
            Name: true,
        },
        IdSupplierHotelMealPlan: true,
        SupplierHotelMealPlan: {
            Name: true,
        },
        IdSupplierHotelRatePlan: true,
        SupplierHotelRatePlan: {
            Name: true,
        },
        IdSupplierTaxType: true,
        SupplierTaxType: {
            Name: true,
            Caption: true,
            Key: true,
        },
        IdSupplierTaxCalculationType: true,
        SupplierTaxCalculationType: {
            Name: true,
            Caption: true,
            LocalizedCaption: true,
            Key: true,
        },
        IdSupplierTaxCalculationRuleType: true,
        SupplierTaxCalculationRuleType: {
            Name: true,
            Caption: true,
            LocalizedCaption: true,
            Key: true,
        },
        IdSupplementCalculationType: true,
        SupplementCalculationType: {
            Name: true,
            Caption: true,
            LocalizedCaption: true,
            Key: true,
        },
        DateLastUpdated: true,
        FromDate: true,
        ToDate: true,
    },
    SupplierSupplements: {
        Id: true,
        Name: true,
        Enabled: true,
        Description: true,
        IdSupplierHotelRoomType: true,
        SupplierHotelRoomType: {
            Name: true,
        },
        IdSupplierHotelMealPlan: true,
        SupplierHotelMealPlan: {
            Name: true,
        },
        IdSupplierHotelRatePlan: true,
        SupplierHotelRatePlan: {
            Name: true,
        },
        IdSupplierTaxCalculationType: true,
        SupplierTaxCalculationType: {
            Name: true,
            Caption: true,
            LocalizedCaption: true,
            Key: true,
        },
        IdSupplierTaxCalculationRuleType: true,
        SupplierTaxCalculationRuleType: {
            Name: true,
            Caption: true,
            LocalizedCaption: true,
            Key: true,
        },
        IdSupplementCalculationType: true,
        SupplementCalculationType: {
            Name: true,
            Caption: true,
            LocalizedCaption: true,
            Key: true,
        },
        DateLastUpdated: true,
        FromDate: true,
        ToDate: true,
    },
    SupplierHotelStopSales: {
        Id: true,
        Enabled: true,
        FromDate: true,
        ToDate: true,
        IdSupplierHotelRoomType: true,
        SupplierHotelRoomType: {
            Name: true,
        },
        IdSupplierHotelMealPlan: true,
        SupplierHotelMealPlan: {
            Name: true,
        },
        IdSupplierHotelRatePlan: true,
        SupplierHotelRatePlan: {
            Name: true,
        },
    },
};

export const PROPERTY_INITIAL_STATE: PropertyState = {
    id: '',
    name: '',
    primary: false,
    idHotel: 0,
    idSupplier: 0,
    hotelCategory: '',
    rating: 0, // decimal
    idHotelType: 0,
    idHotelCategory: 0,
    idHotelChain: 0,
    idCurrency: 0,
    checkInHour: '',
    checkOutHour: '',
    roomCount: 0,
    address: '',
    email: '',
    phone: '',
    bookingPhone: '',
    bookingEmail: '',
    accountingEmail: '',
    accountingPhone: '',
    idCountry: 0,
    idRegion: 0,
    idProvince: 0,
    idCity: 0,
    postalCode: '',
    airportCode: '',
    idAirportCode: '',
    idTimeZone: 0,
    adultsOnly: false,
    adultAge: 0,
    childrenAgeSegments: [],
    descriptions: [],
    services: [],
    zitoneoServices: [],
    hostSegments: [],
    mealPlanTypes: [],
    images: [],
    rooms: [],
    notes: [],
    taxes: [],
    supplements: [],
    stopSales: [],
    attributeDefinitions: [],
    attributeValues: [],
};

const PROGRESS_STEP_INITIAL_STATE: ProgressInfo = {
    percent: 0,
    lastTabCompleted: 1,
};

export const PROGRESS_INITIAL_STATE: ProgressState = {
    totalProgress: 100,
    progressByStep: {
        [PROGRESS_STEP.MAIN_INFORMATION]: PROGRESS_STEP_INITIAL_STATE,
        [PROGRESS_STEP.PUBLIC_PORTRAIT]: PROGRESS_STEP_INITIAL_STATE,
        [PROGRESS_STEP.ROOM_CONFIGURATION]: PROGRESS_STEP_INITIAL_STATE,
    },
    activeStep: '',
    activeTab: 0,
    activeTabKey: '',
    progressArray: [],
};

export const DESCRIPTION_DEATIL_INITIAL_STATE: DescriptionDetailState = {
    id: '',
    idLanguage: 0,
    languageCode: '',
    language: '',
    descriptionContent: '',
    isOpenPanel: false,
};

export const IMAGE_INITIAL_STATE: ImageState = {
    id: '',
    caption: '',
    feature: false,
    published: false,
    is360: false,
    idType: 0,
    type: '',
    url: '',
    file: null,
    idRoom: 0,
};

export const IMAGES_DETAIL_INITIAL_STATE: ImagesDetailState = {
    images: [],
    isOpenPanel: false,
    isDropdownVisible: true,
    isLoading: false,
    category: null,
    idRoom: 0,
};

export const NOTE_DETAIL_INITIAL_STATE: NoteDetailState = {
    id: '',
    message: '',
    fromDate: null,
    toDate: null,
    enabled: false,
    fromBookingDate: false,
    isOpenPanel: false,
};

export const NOTE_DATE_TAG_COLOR: Record<string, TagColor> = {
    old: 'cyan',
    current: 'green',
    upcoming: 'purple',
};

export const NOTE_TYPE_DATE_TAG_COLOR: Record<string, TagColor> = {
    BOOKING_DATE: 'pink',
    CHECK_IN_DATE: 'orange',
};

export const tabsData = {
    [PROGRESS_STEP.MAIN_INFORMATION]: {
        name: PROGRESS_STEP.MAIN_INFORMATION,
        numberOfTabs: 5,
        url: URL_STEP.MAIN_INFORMATION,
        tabs: [
            {
                title: PROGRESS_MAIN_INFORMATION_TAB.COMMERCIAL,
                tabNumber: 1,
            },
            {
                title: PROGRESS_MAIN_INFORMATION_TAB.CONTACT,
                tabNumber: 2,
            },
            {
                title: PROGRESS_MAIN_INFORMATION_TAB.LOCATION,
                tabNumber: 3,
            },
            {
                title: PROGRESS_MAIN_INFORMATION_TAB.AREA,
                tabNumber: 4,
            },
            {
                title: PROGRESS_MAIN_INFORMATION_TAB.PROPERTY,
                tabNumber: 5,
            },
        ],
    },
    [PROGRESS_STEP.PUBLIC_PORTRAIT]: {
        name: PROGRESS_STEP.PUBLIC_PORTRAIT,
        numberOfTabs: 7,
        url: URL_STEP.PUBLIC_PORTRAIT,
        tabs: [
            {
                title: PROGRESS_PUBLIC_PORTRAIT_TAB.KID_POLICIES,
                tabNumber: 1,
            },
            {
                title: PROGRESS_PUBLIC_PORTRAIT_TAB.SEGMENTS,
                tabNumber: 2,
            },
            {
                title: PROGRESS_PUBLIC_PORTRAIT_TAB.SERVICES,
                tabNumber: 3,
            },
            {
                title: PROGRESS_PUBLIC_PORTRAIT_TAB.MEAL_PLANS,
                tabNumber: 4,
            },
            {
                title: PROGRESS_PUBLIC_PORTRAIT_TAB.DESCRIPTIONS,
                tabNumber: 5,
            },
            {
                title: PROGRESS_PUBLIC_PORTRAIT_TAB.SPECIAL_NOTES,
                tabNumber: 6,
            },
            {
                title: PROGRESS_PUBLIC_PORTRAIT_TAB.IMAGES,
                tabNumber: 7,
            },
        ],
    },
    [PROGRESS_STEP.ROOM_CONFIGURATION]: {
        name: PROGRESS_STEP.ROOM_CONFIGURATION,
        numberOfTabs: 6,
        url: URL_STEP.ROOM_CONFIGURATION,
        tabs: [
            {
                title: PROGRESS_ROOM_CONFIGURATION_TAB.ROOM_INFO,
                tabNumber: 1,
            },
            {
                title: PROGRESS_ROOM_CONFIGURATION_TAB.OCCUPANCY,
                tabNumber: 2,
            },
            {
                title: PROGRESS_ROOM_CONFIGURATION_TAB.SERVICES,
                tabNumber: 3,
            },
            {
                title: PROGRESS_ROOM_CONFIGURATION_TAB.BEDS,
                tabNumber: 4,
            },
            {
                title: PROGRESS_ROOM_CONFIGURATION_TAB.DESCRIPTIONS,
                tabNumber: 5,
            },
            {
                title: PROGRESS_ROOM_CONFIGURATION_TAB.IMAGES,
                tabNumber: 6,
            },
        ],
    },
};

interface ProgressStep {
    IdSupplierHotel: number;
    Step: string;
    Tab: string;
    Completed: boolean;
    __isAdded: boolean;
    __isChanged: boolean;
    Id: string;
}

export const setCompletedProgressArray = (IdSupplierHotel: number) => {
    let progressArray: ProgressStep[] = [];
    for (const [stepKey, stepData] of Object.entries(tabsData)) {
        const step = stepKey as PROGRESS_STEP;

        const stepArray = stepData.tabs.map(tab => ({
            IdSupplierHotel,
            Step: step,
            Tab: tab.title,
            Completed: true,
            __isAdded: true,
            __isChanged: true,
            Id: '-1',
        }));

        progressArray = [...progressArray, ...stepArray];
    }
    return progressArray;
};

export const getProgressStateFromDB = (
    progressArray: ProgressArrayItem[]
): ProgressByStepState => {
    const progressState: Partial<ProgressByStepState> = {};

    for (const [stepKey, stepData] of Object.entries(tabsData)) {
        const step = stepKey as PROGRESS_STEP;
        const tabsInStep = stepData.tabs;
        const totalTabs = tabsInStep.length;

        // Encontrar la última tab completada por Step
        const completedItemsForStep = progressArray
            .filter(item => item.step === step)
            .map(item => item.tab);

        // Encontrar el mayor tabNumber completado
        let lastTabCompleted = 0;
        for (const tab of [...tabsInStep].reverse()) {
            if (completedItemsForStep.includes(tab.title)) {
                lastTabCompleted = tab.tabNumber;
                break;
            }
        }

        const percent = Math.round((lastTabCompleted * 100) / totalTabs);

        progressState[step] = {
            percent,
            lastTabCompleted,
        };
    }

    return progressState as ProgressByStepState;
};

//old
export const DDBB_NEW_ROOM_INITIAL_STATE = {
    Id: '-1',
    IdSupplierHotel: '0',
    Name: 'New room',
    Size: 1,
    Description: '',
    AdultOnly: true,
    FreeBaby1: false,
    FreeBaby2: false,
    Occupancy: '',
    MinAdults: 1,
    MaxAdults: 1,
    MinChildren: 0,
    MaxChild: 0,
    StandardOccupancy: 1,
    Services: [],
    SupplierHotelRoomBedTypes: [],
    SupplierHotelRoomTypesImages: [],
};

export const ROOM_INITIAL_STATE: RoomState = {
    id: '',
    name: '',
    occupancy: '',
    adultsOnly: false,
    freeBaby1: false,
    freeBaby2: false,
    standardOccupancy: 0,
    minAdults: 0,
    maxAdults: 0,
    minChildren: 0,
    maxChildren: 0,
    size: 0,
    quantity: 0,
    images: [],
    services: [],
    bedTypes: [],
    descriptions: [],
};

export const getIsDifferentContactInfo = (data: {
    bookingPhone: string;
    bookingEmail: string;
    accountingEmail: string;
    accountingPhone: string;
}): boolean => {
    // Check if any of the contact fields are filled
    // If any of these fields are filled, it means the contact info is different
    // from the general contact info, which is the hotel general phone and email.
    // If all fields are empty, it means the contact info is the same.
    const { bookingPhone, bookingEmail, accountingEmail, accountingPhone } =
        data;
    const isDifferent =
        bookingPhone || bookingEmail || accountingEmail || accountingPhone;
    return !!isDifferent;
};

export const VALIDATIONERRORS_INITIAL_STATE: ValidationErrorsState = {
    name: '',
    phone: '',
    email: '',
    address: '',
    idCountry: '',
    idCity: '',
    postalCode: '',
    idTimezone: '',
    idHotelType: '',
    idHotelCategory: '',
    roomAmount: '',
    idCurrency: '',
    childrenAgeSegments: '',
};

export const getForwardProgress = (prev: ProgressState) => {
    const currentStep = prev.activeStep;
    const currentTab = prev.activeTab;
    if (!currentStep || !currentTab) return prev;
    const stepData = tabsData[currentStep];

    if (!stepData) return prev;

    const currentTabData = (
        stepData.tabs as { tabNumber: number; title: string }[]
    ).find(
        (tab: { tabNumber: number; title: string }) =>
            tab.tabNumber === currentTab
    );
    if (!currentTabData) return prev;

    const isAlreadyCompleted = prev.progressArray.some(
        item => item.step === currentStep && item.tab === currentTabData.title
    );

    const updatedProgressArray = isAlreadyCompleted
        ? [...prev.progressArray]
        : [
              ...prev.progressArray,
              {
                  id: `-${getRandomNumber(1000, 9999)}`,
                  step: currentStep,
                  tab: currentTabData.title,
                  __isAdded: true,
              },
          ];

    // Recalcular progreso por paso
    const completedTabs = updatedProgressArray.filter(
        item => item.step === currentStep
    ).length;
    const updatedProgressByStep = {
        ...prev.progressByStep,
        [currentStep]: {
            percent: Math.round((completedTabs / stepData.numberOfTabs) * 100),
            lastTabCompleted: completedTabs + 1,
        },
    };

    // Recalcular total con límite en 95%
    const rawTotalPercent =
        Object.values(updatedProgressByStep).reduce(
            (sum, s) => sum + s.percent,
            0
        ) / Object.keys(updatedProgressByStep).length;

    const totalPercent = Math.min(Math.round(rawTotalPercent), 95);

    // Calcular siguiente tab o paso
    const nextTab = currentTab + 1;
    const hasMoreTabs = nextTab <= stepData.numberOfTabs;

    if (hasMoreTabs) {
        const nextTabData = (
            stepData.tabs as { tabNumber: number; title: string }[]
        ).find(tab => tab.tabNumber === nextTab);
        return {
            ...prev,
            progressArray: updatedProgressArray,
            progressByStep: updatedProgressByStep,
            totalProgress: totalPercent,
            activeTab: nextTab,
            activeTabKey: (nextTabData?.title ||
                '') as ProgressState['activeTabKey'],
        };
    } else {
        return {
            ...prev,
            progressArray: updatedProgressArray,
            progressByStep: updatedProgressByStep,
            totalProgress: totalPercent,
        };
    }
};

export const getVisibleDescription = (
    descriptions: DescriptionState[],
    appLanguage: AppLanguage
) => {
    if (!descriptions?.length) return null;
    const descriptionInAppLanguage = descriptions.find(
        desc => desc.languageCode === appLanguage
    );
    const descriptionInDefaultLanguage = descriptions.find(
        desc => desc.languageCode === AppLanguage.ENGLISH
    );

    return (
        descriptionInAppLanguage ||
        descriptionInDefaultLanguage ||
        descriptions[0]
    );
};

export const taxesColumns = [
    { id: 'Enabled', label: 'enabled' },
    { id: 'Name', label: 'name' },
    { id: 'SupplierHotelRoomType/Name', label: 'room' },
    { id: 'SupplierHotelMealPlan/Name', label: 'meal_plan' },
    { id: 'SupplierHotelRatePlan/Name', label: 'rate_plan' },
    { id: 'Type', label: 'type' },
    { id: 'Dates', label: 'dates' },
    { id: 'Menu', label: '' },
];

export const stopSalesColumns = [
    { id: 'Enabled', label: 'enabled' },
    { id: 'SupplierHotelRoomType/Name', label: 'room' },
    { id: 'SupplierHotelRatePlan/Name', label: 'rate_plan' },
    { id: 'SupplierHotelMealPlan/Name', label: 'meal_plan' },
    { id: 'Dates', label: 'dates' },
    { id: 'Menu', label: '' },
];
